import type { PublicProfile } from '@ithildin/shared';

export class ApiError extends Error {
  constructor(message: string, readonly status: number) {
    super(message);
  }
}

/**
 * Empty in development, where Vite proxies /api to the local server and the
 * session cookie is same-origin. In production the API runs on its own host
 * (it needs Postgres and a long-lived process, so it cannot sit on Pages),
 * and this points at it.
 */
const API_BASE = import.meta.env.VITE_API_URL?.replace(/\/$/, '') ?? '';

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${API_BASE}/api${path}`, {
    ...init,
    credentials: 'include',
    headers: { 'content-type': 'application/json', ...(init?.headers ?? {}) },
  });

  if (!res.ok) {
    const payload = (await res.json().catch(() => ({}))) as { error?: string };
    throw new ApiError(payload.error ?? `request failed (${res.status})`, res.status);
  }
  return res.status === 204 ? (undefined as T) : ((await res.json()) as T);
}

export interface Resolved {
  chain: string;
  address: string;
  name: string | null;
  profile: PublicProfile;
}

export interface OwnerProfile extends PublicProfile {
  channels: { channel: string; displayHint: string; verified: boolean; linkedAt: string }[];
}

export interface ThreadSummary {
  id: string;
  counterpartyAddress: string;
  counterpartyChain: string;
  counterpartyDisplayName: string | null;
  lastMessageAt: string;
  lastMessagePreview: string;
  unreadCount: number;
  awaitingMyReply: boolean;
  escrow: EscrowView | null;
}

export interface EscrowView {
  id: string;
  status: 'PENDING' | 'RELEASED' | 'REFUNDED';
  amountMicros: string;
  deadlineAt: string;
  /** Start of the reply window, so the UI can show how much of it is left. */
  fundedAt: string;
  /** Set once the recipient replied: the fee is earned, awaiting settlement. */
  repliedAt: string | null;
  chain: string;
  vaultAddress: string | null;
  fundingTxSignature: string | null;
  settlementTxSignature: string | null;
}

export interface ThreadDetail {
  id: string;
  counterpartyAddress: string;
  counterpartyChain: string;
  counterpartyDisplayName: string | null;
  messages: {
    id: string;
    senderAddress: string;
    body: string;
    createdAt: string;
    escrow: EscrowView | null;
  }[];
}

export const api = {
  nonce: (chain: string, address: string) =>
    request<{ nonce: string; issuedAt: string; message: string }>('/auth/nonce', {
      method: 'POST',
      body: JSON.stringify({ chain, address }),
    }),

  verify: (body: { chain: string; address: string; message: string; signature: string }) =>
    request<{ token: string }>('/auth/verify', { method: 'POST', body: JSON.stringify(body) }),

  logout: () => request<void>('/auth/logout', { method: 'POST' }),

  me: () => request<OwnerProfile>('/auth/me'),

  profile: (chain: string, address: string) =>
    request<PublicProfile>(`/profiles/${chain}/${address}`),

  /** Accepts an address on either chain, or an ENS/SNS name. */
  resolve: (q: string) => request<Resolved>(`/profiles/resolve?q=${encodeURIComponent(q)}`),

  updateProfile: (body: Record<string, unknown>) =>
    request<OwnerProfile>('/profiles/me', { method: 'PATCH', body: JSON.stringify(body) }),

  threads: (filter = 'all') =>
    request<{ items: ThreadSummary[] }>(`/threads?filter=${encodeURIComponent(filter)}`),

  thread: (id: string) => request<ThreadDetail>(`/threads/${id}`),

  send: (body: Record<string, unknown>) =>
    request<{ threadId: string; messageId: string }>('/threads', {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  reply: (id: string, text: string) =>
    request<{ messageId: string; escrowsUnlocked: number }>(`/threads/${id}/reply`, {
      method: 'POST',
      body: JSON.stringify({ body: text }),
    }),

  linkChannel: (channel: string) =>
    request<{ url: string }>('/channels/link', {
      method: 'POST',
      body: JSON.stringify({ channel }),
    }),

  unlinkChannel: (channel: string) =>
    request<void>(`/channels/${channel}`, { method: 'DELETE' }),

  setPrefs: (body: Record<string, unknown>) =>
    request<unknown>('/channels/prefs', { method: 'PATCH', body: JSON.stringify(body) }),
};
